import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { useFormikContext } from 'formik';
import isEqual from 'lodash-es/isEqual';

import editIcon from 'core/assets/svg/edit.svg';
import { addressFormatter } from 'core/helpers/formatters';
import { NotificationCard } from 'core/components/notification-card';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import smartyStreetsAPI from 'core/helpers/smartyStreetsAPI';

import useStyles from './mailing-address-notification-card.styles';

const MailingAddressCard = ({ mailingAddress }) => {
  const classes = useStyles();
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [showNoAptNumberCheckbox, setShowNoAptNumberCheckbox] = useState(false);
  const { values, setFieldValue, setFieldError } = useFormikContext();

  const address2 =
    values.mailingAddress?.address2?.trim() === 'Apt' ? '' : values.mailingAddress?.address2?.trim() || '';
  const disableNoAptCheckbox = address2.length !== 0;

  useEffect(() => {
    const validateAddress = async () => {
      if (!editAddressOpen) {
        return;
      }
      // validate if address needs unit number or not (only if address has changed)
      const addressChanged = !isEqual(values.mailingAddress, mailingAddress);
      if (addressChanged && values.mailingAddress?.address) {
        const { needsUnitNumber } = await smartyStreetsAPI.validateAddress(values.mailingAddress);
        if (needsUnitNumber && !values.noAptNumber && !values.mailingAddress?.address2) {
          setFieldError('mailingAddress.address2', 'This address may have an apt, unit, floor, building, suite, etc.');
          setShowNoAptNumberCheckbox(true);
        } else {
          // Clear error when address is valid or address2 is filled
          setFieldError('mailingAddress.address2', undefined);
          if (!needsUnitNumber) {
            setShowNoAptNumberCheckbox(false);
          }
        }
      }
    };

    validateAddress();
  }, [values.mailingAddress, mailingAddress, editAddressOpen, values.noAptNumber, setFieldError]);

  return (
    <NotificationCard
      type="light"
      header={
        <span>
          Is <em>{addressFormatter(mailingAddress)}</em> still the right mailing address?
        </span>
      }
      iconSize="icon16"
      iconClassName={classes.icon}
      noShadow
      className={classes.cardContainer}
      hideIcon={editAddressOpen}
    >
      {!editAddressOpen && (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Label className={classes.notificationText}>
              If this isn't correct, refund checks and final documents may not reach the member
            </Label>
          </Grid>

          <Grid item>
            <Button
              color="secondary"
              variant="text"
              className={classes.button}
              onClick={() => setEditAddressOpen(true)}
            >
              <img src={editIcon} className={classes.editIcon} alt="edit mailing address" />
              Edit mailing address
            </Button>
          </Grid>
        </Grid>
      )}

      {editAddressOpen && (
        <Grid container spacing={2}>
          <FormField
            mode="light"
            name="mailingAddress"
            id="mailingAddress.address"
            label="Address"
            type="address"
            xs={12}
            permissions={{ isLicensedAction: false }}
            ignoreGlobalDisabledState={true}
          />
          <Grid item xs={12}>
            <LabelTooltip
              label="Apt, unit, floor, building, etc."
              tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.address2 }}
              mode="light"
            >
              <FormField
                mode="light"
                name="mailingAddress.address2"
                id="mailingAddress.address2"
                type="string"
                optional
                permissions={{ isLicensedAction: false }}
                disabled={values.noAptNumber}
                ignoreGlobalDisabledState={true}
              />
            </LabelTooltip>
          </Grid>
          {showNoAptNumberCheckbox && (
            <FormField
              mode="light"
              name="noAptNumber"
              id="noAptNumber"
              label="Address does not have apt/unit no., etc."
              type="checkbox"
              optional
              xs={12}
              permissions={{ isLicensedAction: false }}
              ignoreGlobalDisabledState={true}
              disabled={disableNoAptCheckbox}
              fast={false}
              onChange={(e) => {
                if (e.target.checked) {
                  setFieldValue('mailingAddress.address2', '');
                }
              }}
            />
          )}
          <FormField
            mode="light"
            name="mailingAddress.city"
            id="mailingAddress.city"
            label="City"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
            ignoreGlobalDisabledState={true}
          />
          <FormField
            mode="light"
            name="mailingAddress.state"
            id="mailingAddress.state"
            label="State"
            type="state"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
            ignoreGlobalDisabledState={true}
          />
          <FormField
            mode="light"
            name="mailingAddress.zip"
            id="mailingAddress.zip"
            label="Zip Code"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
            ignoreGlobalDisabledState={true}
          />
        </Grid>
      )}
    </NotificationCard>
  );
};

export default MailingAddressCard;
